import React, { useState } from "react";
import { Link } from "react-router-dom";
import SEOHead from "../../components/SEO/SEOHead";
import {
  generateBreadcrumbStructuredData,
  generateWebPageStructuredData,
} from "../../components/SEO/StructuredData";
import LegalPageLayout from "../../components/Legal/LegalPageLayout";
import { SUPPORT_EMAIL } from "../../components/SEO/siteOrigin";

const LAST_UPDATED = "October 31, 2026";
const DESCRIPTION =
  "Pay now refunds for bowlers and directors, and renewal, cancel, and refunds for organization plans.";

const RefundPolicy = () => {
  const [activeSection, setActiveSection] = useState("platform-role");

  const structuredData = generateWebPageStructuredData({
    name: "Refund & Cancellation Policy",
    description: DESCRIPTION,
    url: "/refund-policy",
    dateModified: "2026-10-31",
  });
  const breadcrumbData = generateBreadcrumbStructuredData([
    { label: "Home", path: "/" },
    { label: "Refund policy", path: "/refund-policy" },
  ]);

  const sections = [
    { id: "platform-role", title: "Who takes the payment" },
    { id: "for-bowlers", title: "For bowlers" },
    { id: "for-directors", title: "For directors" },
    { id: "stripe-role", title: "What Stripe does" },
    { id: "platform-limitations", title: "What we cannot do" },
    { id: "organization-plans", title: "Organization plans" },
  ];

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <>
      <SEOHead
        title="Refund & Cancellation Policy - Bowling Tournament App"
        description={DESCRIPTION}
        keywords={[
          "refund policy",
          "cancellation policy",
          "tournament refunds",
          "Pay now",
          "Card payments",
        ]}
        canonicalUrl="/refund-policy"
        noIndex={false}
        additionalMeta={[{ name: "last-modified", content: LAST_UPDATED }]}
        structuredData={structuredData}
        additionalStructuredData={[breadcrumbData]}
      />
      <LegalPageLayout
        title="Refund & Cancellation Policy"
        lastUpdated={LAST_UPDATED}
        sections={sections}
        activeSection={activeSection}
        onSectionClick={scrollToSection}
      >
        <div className="space-y-12">
          <section id="platform-role">
            <h2 className="heading-display text-xl mb-4">
              Who takes the payment
            </h2>
            <div className="max-w-none text-brand-charcoal leading-relaxed">
              <p>
                The organization that runs the tournament is the merchant of
                record. Bowling Tournament App is the software platform and
                does not hold entry money.
              </p>
              <p className="mt-4">
                If the organization turned on Card payments, you can pay with
                Pay now. Stripe charges your card for that organization. Pay
                now includes a 2% platform fee plus card processing.
              </p>
              <p className="mt-4">
                Other payment options, if any, are set by the organization.
                This app does not add a platform fee or card processing to
                those payments.
              </p>
            </div>
          </section>

          <section id="for-bowlers">
            <h2 className="heading-display text-xl mb-4">For bowlers</h2>
            <div className="max-w-none text-brand-charcoal leading-relaxed">
              <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3">
                Paid outside Pay now
              </h3>
              <p className="mb-4">
                Ask the organization that runs the tournament. Those refunds
                follow that organization’s rules and the tournament rules.
              </p>

              <h3 className="font-display font-bold text-brand-charcoal text-lg mt-8 mb-3">
                Paid with Pay now
              </h3>
              <p className="mb-4">
                On My Signups or Cart, you can Cancel until 24 hours before
                the squad. That refunds the card and removes the signup. The
                organization cannot turn that window off. Tournament rules do
                not block Cancel in that window.
              </p>
              <p className="mb-4">
                A Pay now refund returns the advertised entry and the 2%
                platform fee. Card processing is not refunded. Advertised
                entry is the entry, sidepots, and brackets on that charge. The
                organization refunds those amounts. We return the 2%. Stripe
                keeps card processing.
              </p>
              <p className="mb-4">
                After that cutoff, use Request a refund. The director sees it
                on Signup Dashboard and refunds from Signup Review. They can
                Refund and remove, or Refund and stay. Those later refunds
                follow the same amounts. The director and the tournament rules
                can say no.
              </p>
              <div className="card-inset border-l-4 border-brand-charcoal p-4 my-6">
                <p className="text-brand-charcoal">
                  <strong>Important:</strong> Clicking Pay now means you agree
                  to the{" "}
                  <Link
                    to="/terms-of-service"
                    className="text-brand-gold-ink underline"
                  >
                    Terms of Service
                  </Link>{" "}
                  and this Refund policy.
                </p>
              </div>
            </div>
          </section>

          <section id="for-directors">
            <h2 className="heading-display text-xl mb-4">For directors</h2>
            <div className="max-w-none text-brand-charcoal leading-relaxed">
              <p className="mb-4">
                Card payments is optional. Desk and other payment methods stay
                under your tournament rules.
              </p>
              <p className="mb-4">
                If you turn on Card payments, Pay now uses this app’s refund
                window. A bowler who paid with Pay now can Cancel until 24
                hours before the squad. This app refunds the advertised entry
                and the 2% platform fee and removes the signup. You cannot
                turn that window off. Clicking Set up card payments means you
                agree to that.
              </p>
              <p className="mb-4">
                Pay now estimates card processing at Stripe’s typical US rate
                of 2.9% plus 30 cents so you net the advertised entry. If
                Stripe takes more, you receive less than advertised. If Stripe
                takes less, you receive more. A Pay now refund still returns
                the advertised entry and the 2% platform fee. It does not use
                Stripe’s actual processing.
              </p>
              <p className="mb-4">
                Add to cart holds a squad spot for 24 hours. Pay now or Pay at
                the desk closes Cart. If they do neither, the hold ends and a
                waitlist signup can take the spot. We email once when 12 hours
                remain. Payment required to hold turns Pay at the desk off.
              </p>
              <p className="mb-4">
                After the 24-hour cutoff, you decide. Use Signup Review to
                Refund and remove or Refund and stay, or the bowler can
                Request a refund. Your tournament rules can guide those later
                refunds.
              </p>
              <p className="mb-4">
                If you refund in Stripe instead of Signup Review, the signup
                is marked Refunded here too. Do not refund twice. A full
                refund in Stripe can send card processing back to the bowler.
                Signup Review does not.
              </p>
            </div>
          </section>

          <section id="stripe-role">
            <h2 className="heading-display text-xl mb-4">What Stripe does</h2>
            <div className="max-w-none text-brand-charcoal leading-relaxed">
              <p className="mb-4">
                Stripe charges the card for the organization. Stripe does not
                set the 24-hour Cancel window. That window is how Pay now
                works in this app.
              </p>
              <p className="mb-4">
                Stripe does not return card processing when a payment is
                refunded. That is why a Pay now refund does not include Card
                processing.
              </p>
              <p className="mb-4">
                Stripe and the organization handle card disputes. This app
                does not decide them.
              </p>
            </div>
          </section>

          <section id="platform-limitations">
            <h2 className="heading-display text-xl mb-4">What we cannot do</h2>
            <div className="max-w-none text-brand-charcoal leading-relaxed">
              <p className="mb-4">
                We do not hold entry money. A Pay now refund goes back on your
                card through Stripe, from the organization.
              </p>
              <p className="mb-4">
                We cannot make an organization refund a desk or cash payment.
              </p>
              <p className="mb-4">
                If Card payments is on, we cannot turn off Cancel until 24
                hours before the squad.
              </p>
              <p className="mb-4">
                We cannot decide a card dispute. Stripe and the organization
                handle that.
              </p>
            </div>
          </section>

          <section id="organization-plans">
            <h2 className="heading-display text-xl mb-4">Organization plans</h2>
            <div className="max-w-none text-brand-charcoal leading-relaxed">
              <p className="mb-4">
                AI Assist and Priority are optional paid plans for the
                organization. Creating a tournament does not require a paid
                plan. Free organizations can create up to 5 tournaments per
                year. These charges are separate from Pay now. Pay now entry
                fees are still collected by the organization. Bowling Tournament
                App sells the organization plan, and Stripe bills the card added
                at checkout.
              </p>
              <p className="mb-4">
                AI Assist is $99 per year and includes up to 20 tournaments
                per year. Priority is $149 per year, includes unlimited
                tournaments, and includes AI Assist. The plan renews each year
                at the price shown at checkout until you cancel. You are charged
                again on the renewal date unless you cancel before then. We
                email the receipt when a plan charge is paid. We email again 30
                days before the plan renews. The invoice is also on Plan and in
                Manage billing.
              </p>
              <p className="mb-4">
                A beta grant is complimentary or promotional organization-plan
                access. It is not a paid subscription. We may modify or end a
                beta grant. Paid-plan refund rules do not apply to beta grants
                unless we state otherwise for that grant.
              </p>
              <p className="mb-4">
                To cancel, open Plan and choose Manage billing. The plan
                stays on until the end of the year you already paid. Average
                import, signup sheet import, and recap sheet import stay
                available until that date. After that, new imports stop. The
                first tournament still includes AI Assist.
              </p>
              <p className="mb-4">
                Manage billing can switch AI Assist to Priority. The difference
                for the rest of the year is charged then. Switching from
                Priority to AI Assist keeps Priority until the end of the year
                you already paid. There is no credit for unused Priority time.
              </p>
              <p className="mb-4">
                If a renewal charge fails, AI imports stay on while Stripe
                retries the card. Update the card in Manage billing. If the
                plan becomes unpaid or canceled, new imports stop. Averages,
                signup sheets, and scores already imported stay. Canceling does
                not delete them. A beta grant stays on without a paid plan.
              </p>
              <p className="mb-4">
                There is no automatic refund window. Email {SUPPORT_EMAIL} to
                ask about a refund of a plan charge. A refund does not delete
                imported data. A plan charge is not refunded because a
                tournament was created.
              </p>
            </div>
          </section>

          <section className="border-t border-brand-charcoal/10 pt-8">
            <h2 className="heading-display text-xl mb-4">Contact & Updates</h2>
            <div className="max-w-none text-brand-charcoal leading-relaxed">
              <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3">
                Questions about refunds?
              </h3>
              <p className="mb-4">
                Bowlers: for a Pay now charge, start on My Signups. For a
                payment the organization collected another way, contact the
                organization using the tournament page. Directors: start on
                Signup Review or Card payments. For an organization plan charge,
                refund, or billing question, email {SUPPORT_EMAIL}. For
                questions about this refund policy, email {SUPPORT_EMAIL}.
                Privacy requests stay at privacy@bowlingtournamentapp.com (see
                the Privacy Policy).
              </p>
              <p className="font-medium">Email: {SUPPORT_EMAIL}</p>
              <p className="text-sm text-brand-muted">
                We aim to respond as soon as reasonably practicable.
              </p>

              <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                Policy Updates
              </h3>
              <p>
                We may update this refund policy to reflect changes in our
                services or legal requirements. Material changes are posted on
                this page with an updated “Last Updated” date. Continued use
                after the effective date means you accept the updated policy.
                If you disagree, stop using the service and cancel under the
                cancel rules in this policy.
              </p>
              <p className="font-medium mt-4">
                Last Updated: {LAST_UPDATED}
              </p>
            </div>
          </section>
        </div>
      </LegalPageLayout>
    </>
  );
};

export default RefundPolicy;
